import { Tooltip } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import React from 'react';

import { ISimpleOptionSelect } from '~/src/dash/components/common';
import {
  ISimpleDropdownOptionSelect,
  SimpleDropdown,
} from '~/src/dash/components/dropdowns/SimpleDropdown';
import { SimpleSingleSelect } from '~/src/dash/components/dropdowns/SimpleSingleSelect';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import {
  ButtonV2,
  Col,
  Cols,
  spacingV2,
  Text,
  TextButton,
  TextInput,
  usePanel,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { IListItem } from '~/src/dash/types/menuList';
import { publicaColors } from '~/src/dash/utils/colors';
import { IncludeExcludeStatus } from '~/src/dash/views/reporting/ReportBuilder/common';

interface IFilterRowProps {
  keys: Array<ISimpleOptionSelect>; // [{key: string, label: string, selected: boolean}]
  onKeyChange: (v: string) => void; // do sth when an option is clicked (passing the key of the )
  values: Array<ISimpleDropdownOptionSelect>;
  onValueChange: (v: string | string[]) => void; // do sth when a value is clicked (passing in the option's key) / or when the input change (new input string)
  valueType: string; // 'input' / 'list'
  valueLabel?: string; // only for when valueType === 'input'
  valueInput?: string; // only for when valueType === 'input'
  isIDDisplayed?: boolean; // display ID for each filter option
  isHideInactive?: boolean; // display the Hide Active toggle or not
  isHideInactiveEnabled?: boolean; // display the Hide Active toggle or not
  onHideInactiveChange?: () => void; // do sth when the Hide Active toogle changes
  isIncludeExclude?: IncludeExcludeStatus; // display the Hide Active toggle or not
  isIncludeExcludeEnabled?: boolean; // display the Hide Active toggle or not
  onIncludeExcludeChange?: () => void; // do sth when Include/Exclude toogle changes
  isSelectAllEnabled?: boolean; // display "Select All" option
  onClear: () => void;
  onRemoveRow: () => void;
  onFilterSearch?: (search: string) => void; // do sth when you search the filter
  tooltip: string;
  maxSelectedItems?: number; // If 1 it converts the dropdown to single select, max select limit not supported yet.
}

export const convertListItemsToDropdownSelectOptions = (
  options: IListItem[],
  IsHideInactiveEnabled: boolean,
): ISimpleDropdownOptionSelect[] => {
  return options.map((item) => {
    return {
      key: item.ID,
      label: item.title,
      value: item.ID,
      isSelected: item.selected,
      ...(IsHideInactiveEnabled ? { active: item.active } : {}),
    };
  });
};

export const FilterRow = ({
  keys,
  onKeyChange,
  values,
  onValueChange,
  valueType,
  valueLabel,
  valueInput,
  isIDDisplayed,
  isHideInactive,
  isHideInactiveEnabled,
  onHideInactiveChange,
  isIncludeExclude,
  isIncludeExcludeEnabled,
  onIncludeExcludeChange,
  isSelectAllEnabled,
  onClear,
  onRemoveRow,
  onFilterSearch,
  tooltip,
  maxSelectedItems,
}: IFilterRowProps) => {
  const { width } = usePanel();
  const { t } = usePublicaTranslation();

  const getIncludeExcludeStatusText = (status: IncludeExcludeStatus | undefined): string => {
    switch (status) {
      case IncludeExcludeStatus.INCLUDE:
        return t('Include');
      case IncludeExcludeStatus.EXCLUDE:
        return t('Exclude');
      default:
        return '';
    }
  };

  return (
    <Cols>
      <Col flex>
        <Cols wrap>
          {/* DROPDOWN 1 */}
          <Col
            style={{
              width: '12.5rem',
              paddingBottom: width && width < 807 ? spacingV2.small : '',
            }}
          >
            <SimpleSingleSelect
              inputWidth='100%'
              options={keys}
              onChange={onKeyChange}
              popoverWidth='12.5rem'
              isSearchEnabled
              isSortEnabled
              style={{ backgroundColor: publicaColors.white }}
            />
          </Col>
          {width && width > 720 && (
            <Col displayFlex style={{ alignItems: 'center' }}>
              <div
                style={{
                  height: '1px',
                  width: '1.5rem',
                  backgroundColor: publicaColors.lightGray,
                }}
              ></div>
            </Col>
          )}

          {/* DROPDOWN 2 */}
          <Col flex>
            <Cols gap={spacingV2.large}>
              <Col
                style={{
                  minWidth: '12.5rem',
                  maxWidth: '22.5rem',
                  width: '100%',
                  paddingBottom: width && width < 695 ? spacingV2.small : '',
                }}
              >
                {valueType !== 'input' && (
                  <SimpleDropdown
                    options={values}
                    label={valueLabel}
                    onChange={onValueChange}
                    onSearchUpdate={onFilterSearch}
                    onClear={onClear}
                    isIDDisplayed={isIDDisplayed}
                    isHidingInactive={isHideInactive}
                    isSelectAllEnabled={isSelectAllEnabled}
                    maxNumberSelected={maxSelectedItems === -1 ? undefined : maxSelectedItems}
                    style={{
                      width: '100%',
                      backgroundColor: publicaColors.veryLightGray,
                      minHeight: '2.055rem',
                    }}
                  />
                )}
                {valueType === 'input' && (
                  <TextInput
                    width='100%'
                    inputSize='small'
                    initialValue={valueInput}
                    placeholder={'(can be comma separated)'}
                    handleOnChange={(input) => onValueChange(input)}
                    inputStyle={{
                      minWidth: '12.5rem',
                      paddingTop: spacingV2.extraSmall,
                      paddingBottom: spacingV2.extraSmall,
                      backgroundColor: publicaColors.veryLightGray,
                      border: `1px solid ${publicaColors.lightGray}`,
                      minHeight: '2.055rem',
                    }}
                  />
                )}
              </Col>
              <Col
                displayFlex
                style={{
                  alignItems: 'center',
                }}
              >
                {tooltip.length > 0 && (
                  <Tooltip title={tooltip} placement='bottom-start'>
                    <InfoIcon
                      style={{
                        verticalAlign: 'bottom',
                        width: '1rem',
                        color: publicaColors.gray,
                        paddingTop: '3px',
                      }}
                    />
                  </Tooltip>
                )}
              </Col>
              {/* HIDE INACTIVE */}
              <Col
                displayFlex
                style={{
                  width: '8rem',
                  alignItems: 'center',
                }}
              >
                {isHideInactiveEnabled && (
                  <TextButton
                    onClick={() => onHideInactiveChange && onHideInactiveChange()}
                    size='small'
                    color={publicaColors.darkGray}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <IconV2
                      name={isHideInactive ? 'eyeoff' : 'eye'}
                      style={{ marginRight: spacingV2.extraSmall }}
                    />
                    <Text
                      tKey={isHideInactive ? 'HideInactive' : 'ShowInactive'}
                      small
                      style={{ color: 'inherit' }}
                    />
                  </TextButton>
                )}
              </Col>
            </Cols>
          </Col>
          {/* INCLUDE/EXCLUDE */}
          <Col
            displayFlex
            style={{
              alignItems: 'center',
              justifyContent: 'center',
            }}
            responsive
            largeStyle={{
              paddingLeft: spacingV2.large,
              paddingRight: spacingV2.large,
            }}
            mediumStyle={{
              padding: '0px',
            }}
          >
            {isIncludeExcludeEnabled && (
              <TextButton
                onClick={() => onIncludeExcludeChange && onIncludeExcludeChange()}
                size='small'
                color={publicaColors.darkGray}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {isIncludeExclude === IncludeExcludeStatus.INCLUDE && (
                  <IconV2
                    name='available'
                    small
                    color={publicaColors.green}
                    style={{ marginRight: spacingV2.extraSmall }}
                  />
                )}
                {isIncludeExclude === IncludeExcludeStatus.EXCLUDE && (
                  <IconV2
                    name='unavailable'
                    small
                    color={publicaColors.red}
                    style={{ marginRight: spacingV2.extraSmall }}
                  />
                )}
                {getIncludeExcludeStatusText(isIncludeExclude)}
              </TextButton>
            )}
          </Col>
        </Cols>
      </Col>

      <Col marginLeft={spacingV2.base}>
        <ButtonV2
          clearBackground
          style={{ height: '1.875rem', display: 'flex', alignItems: 'center' }}
          onClick={onRemoveRow}
        >
          <IconV2 name='close' />
        </ButtonV2>
      </Col>
    </Cols>
  );
};
